import { CalendarHeart, Pencil, Trash2 } from 'lucide-react';
import { DetailPanel } from '@/components/common/DetailPanel';
import { EmptyState } from '@/components/common/EmptyState';
import { IconTooltipButton } from '@/components/common/IconTooltipButton';
import { ListSkeleton } from '@/components/common/ListSkeleton';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import { DARK_BLUE_BAND_SKELETON_CLASS } from '@/styles/darkBlueBand';
import { COUNT_CHIP_CLASS, COUNT_CHIP_ON_DARK_CLASS } from '@/styles/segmentedControl';
import { formatNumber, pluralize } from '@/utils/number';
import { INACTIVE_HOLIDAY_NAME_CLASS } from '../constants/holiday.constants';
import type { Holiday } from '../types/holiday.types';
import { formatHolidayLongDates, getHolidayDayCount } from '../utils/holidayCalendar';

const LOADING_ITEM_COUNT = 3;
const INACTIVE_LABEL = 'Inactive, not counted as a day off';
const INACTIVE_ROW_CLASS = 'opacity-55';

interface HolidayMonthListProps {
  monthLabel: string;
  holidays: Holiday[] | undefined;
  className?: string | undefined;
  onEditHoliday: (holiday: Holiday) => void;
  onDeleteHoliday: (holiday: Holiday) => void;
}

export function HolidayMonthList({
  monthLabel,
  holidays,
  className,
  onEditHoliday,
  onDeleteHoliday,
}: HolidayMonthListProps) {
  return (
    <DetailPanel
      title={`Holidays in ${monthLabel}`}
      icon={CalendarHeart}
      hasTintedHeader
      headerTint="dark"
      size="compact"
      className={cn('pb-2', className)}
      titleAddon={<HolidayCountChip holidays={holidays} />}
    >
      <div className="-mt-2 lg:min-h-0 lg:overflow-x-hidden lg:overflow-y-auto lg:overscroll-contain">
        <HolidayMonthListBody
          holidays={holidays}
          onEditHoliday={onEditHoliday}
          onDeleteHoliday={onDeleteHoliday}
        />
      </div>
    </DetailPanel>
  );
}

interface HolidayCountChipProps {
  holidays: Holiday[] | undefined;
}

function HolidayCountChip({ holidays }: HolidayCountChipProps) {
  if (holidays === undefined) {
    return (
      <Skeleton aria-hidden className={cn('h-5 w-6 rounded-full', DARK_BLUE_BAND_SKELETON_CLASS)} />
    );
  }

  return (
    <span className={cn(COUNT_CHIP_CLASS, COUNT_CHIP_ON_DARK_CLASS)}>
      {formatNumber(holidays.length)}
      <span className="sr-only"> {pluralize(holidays.length, 'holiday')}</span>
    </span>
  );
}

type HolidayMonthListBodyProps = Omit<HolidayMonthListProps, 'monthLabel' | 'className'>;

function HolidayMonthListBody({
  holidays,
  onEditHoliday,
  onDeleteHoliday,
}: HolidayMonthListBodyProps) {
  if (holidays === undefined) return <ListSkeleton itemCount={LOADING_ITEM_COUNT} />;

  if (holidays.length === 0) {
    return (
      <EmptyState
        size="compact"
        icon={CalendarHeart}
        title="No holidays this month"
        description="Pick a day on the calendar to add one."
      />
    );
  }

  return (
    <ul className="divide-y divide-dashed">
      {holidays.map((holiday) => (
        <HolidayListItem
          key={holiday.id}
          holiday={holiday}
          onEditHoliday={onEditHoliday}
          onDeleteHoliday={onDeleteHoliday}
        />
      ))}
    </ul>
  );
}

interface HolidayListItemProps {
  holiday: Holiday;
  onEditHoliday: (holiday: Holiday) => void;
  onDeleteHoliday: (holiday: Holiday) => void;
}

function HolidayListItem({ holiday, onEditHoliday, onDeleteHoliday }: HolidayListItemProps) {
  const dayCount = getHolidayDayCount(holiday);

  return (
    <li className="flex items-start justify-between gap-3 py-3">
      <div className={cn('flex min-w-0 flex-col gap-1', !holiday.isActive && INACTIVE_ROW_CLASS)}>
        <p className="text-sm font-semibold tabular-nums">
          {formatHolidayLongDates(holiday)}
          {dayCount > 1 ? (
            <span className="font-normal text-muted-foreground">
              {' '}
              · {dayCount} {pluralize(dayCount, 'day')}
            </span>
          ) : null}
        </p>
        <p
          className={cn(
            'text-xs wrap-break-word text-muted-foreground',
            !holiday.isActive && INACTIVE_HOLIDAY_NAME_CLASS,
          )}
        >
          {holiday.name}
          {holiday.isActive ? null : <span className="sr-only">, {INACTIVE_LABEL}</span>}
        </p>
      </div>
      <div className="-mt-1 flex shrink-0 items-center">
        <IconTooltipButton
          icon={Pencil}
          label={`Edit ${holiday.name}`}
          onClick={() => onEditHoliday(holiday)}
          hasTooltip={false}
        />
        <IconTooltipButton
          icon={Trash2}
          label={`Delete ${holiday.name}`}
          onClick={() => onDeleteHoliday(holiday)}
          hasTooltip={false}
          isDanger
        />
      </div>
    </li>
  );
}
